import React, { useMemo, useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWR, { mutate as globalMutate } from 'swr';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Loader2, Lock, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button, buttonVariants } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Combobox, type ComboboxOption } from '@/components/ui/combobox';
import { Select } from '@/components/ui/select';
import { useT } from '@/contexts/I18nContext';
import { usePageTitle } from '@/contexts/PageHeaderContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  adminSetUserPassword,
  clearAdminUserTelegramBinding,
  getAdminUser,
  grantUserZone,
  patchAdminUser,
  revokeUserZone,
  setAdminUserTelegramBinding,
  updateUserZoneRole,
  type AdminUserDetail,
  type AdminUserZoneMembership,
} from '@/api/adminUsers';
import { listZones, type ZoneDto } from '@/api/zones';
import { addUserGroupMember, listUserGroups, removeUserGroupMember } from '@/api/adminGroups';
import { ZONES_SWR_KEY } from '@/hooks/useCurrentZone';
import { extractApiError } from '@/utils/apiError';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/tooltip';

const PASSWORD_MIN = 8;
const ZONE_ROLES = ['zone_employee', 'zone_manager', 'zone_admin', 'line_worker', 'chain_office'];

export default function UserAdminPage() {
  const { t } = useT();
  const { userId } = useParams<{ userId: string }>();
  usePageTitle(t('admin.user.title'), t('admin.user.subtitle'));

  const swrKey = userId ? `admin-user:${userId}` : null;
  const { data: user, error, isLoading, mutate } = useSWR(
    swrKey,
    () => getAdminUser(userId!).then(r => r.data),
  );

  return (
    <div className="container mx-auto max-w-3xl space-y-4 px-4 py-6">
      <div>
        <Link
          to="/admin/users"
          className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'gap-1.5')}
        >
          <ArrowLeft className="size-3.5" />
          {t('admin.user.backToList')}
        </Link>
      </div>

      {isLoading && (
        <Card>
          <CardContent className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
          </CardContent>
        </Card>
      )}

      {error && !isLoading && !user && (
        <Card>
          <CardContent>
            <ServerStatusNotice variant="card" error={error} onRetry={() => mutate()} />
          </CardContent>
        </Card>
      )}

      {user && (
        <>
          <ProfileCard user={user} onChanged={() => mutate()} />
          <ResetPasswordCard userId={user.id} username={user.username} />
          <TelegramBindingCard user={user} onChanged={() => mutate()} />
          <UserGroupsCard user={user} onChanged={() => mutate()} />
          <BranchesCard user={user} onChanged={() => mutate()} />
        </>
      )}
    </div>
  );
}

// ── Profile + role flags ──────────────────────────────────────────────────────

function ProfileCard({ user, onChanged }: { user: AdminUserDetail; onChanged: () => void }) {
  const { t } = useT();
  const { user: currentUser, refresh } = useCurrentUser();
  const isSelf = currentUser?.id === user.id;

  const [email, setEmail] = useState(user.email);
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSaved, setEmailSaved] = useState(false);

  const [flagsBusy, setFlagsBusy] = useState<'is_superuser' | 'is_staff' | null>(null);
  const [flagsError, setFlagsError] = useState<string | null>(null);

  const emailDirty = email !== user.email;

  async function handleSaveEmail() {
    setEmailError(null);
    setEmailSaved(false);
    setSavingEmail(true);
    try {
      await patchAdminUser(user.id, { email: email.trim() });
      setEmailSaved(true);
      onChanged();
    } catch (err) {
      setEmailError(extractApiError(err, t('error.general')));
    } finally {
      setSavingEmail(false);
    }
  }

  async function handleToggleFlag(field: 'is_superuser' | 'is_staff', next: boolean) {
    setFlagsError(null);
    setFlagsBusy(field);
    try {
      await patchAdminUser(user.id, { [field]: next });
      onChanged();
      // If we touched our own flags, refresh /me/ so the sidebar updates.
      if (isSelf) refresh();
    } catch (err) {
      setFlagsError(extractApiError(err, t('error.general')));
    } finally {
      setFlagsBusy(null);
    }
  }

  return (
    <Card variant="section">
      <CardHeader>
        <CardTitle>{t('admin.user.profile')}</CardTitle>
        <CardDescription>
          {t('admin.user.profileDescription', { username: user.username })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid gap-3 text-sm sm:grid-cols-[140px,1fr]">
          <div className="text-muted-foreground">{t('account.username')}</div>
          <div className="font-medium">{user.username}</div>
        </div>

        <div className="grid gap-1.5 sm:max-w-md">
          <Label htmlFor="admin-user-email">{t('account.email')}</Label>
          <div className="flex gap-2">
            <Input
              id="admin-user-email"
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setEmailSaved(false); }}
              autoComplete="off"
            />
            <Button
              type="button"
              size="sm"
              disabled={!emailDirty || savingEmail}
              onClick={handleSaveEmail}
            >
              {savingEmail ? t('common.saving') : t('common.save')}
            </Button>
          </div>
          {emailError && <p className="text-xs text-destructive">{emailError}</p>}
          {emailSaved && (
            <p className="inline-flex items-center gap-1 text-xs text-emerald-600">
              <Check className="size-3" /> {t('common.saved')}
            </p>
          )}
        </div>

        <div className="space-y-3 border-t pt-4">
          <FlagRow
            id="flag-is-superuser"
            label={t('admin.user.flagSuperuser')}
            description={t('admin.user.flagSuperuserDescription')}
            checked={user.is_superuser}
            disabled={flagsBusy != null || (isSelf && user.is_superuser)}
            disabledHint={isSelf && user.is_superuser ? t('admin.user.cannotSelfRevoke') : null}
            busy={flagsBusy === 'is_superuser'}
            onChange={next => handleToggleFlag('is_superuser', next)}
          />
          <FlagRow
            id="flag-is-staff"
            label={t('admin.user.flagStaff')}
            description={t('admin.user.flagStaffDescription')}
            checked={user.is_staff}
            disabled={flagsBusy != null || user.is_superuser}
            disabledHint={user.is_superuser ? t('admin.user.staffImpliedBySuperuser') : null}
            busy={flagsBusy === 'is_staff'}
            onChange={next => handleToggleFlag('is_staff', next)}
          />
          {flagsError && <p className="text-xs text-destructive">{flagsError}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function FlagRow({
  id, label, description, checked, disabled, disabledHint, busy, onChange,
}: {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  disabledHint?: string | null;
  busy?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <Label htmlFor={id} className="text-sm font-medium">{label}</Label>
        <p className="text-xs text-muted-foreground">{description}</p>
        {disabled && disabledHint && (
          <p className="mt-0.5 text-xs italic text-muted-foreground">{disabledHint}</p>
        )}
      </div>
      <div className="flex items-center gap-2">
        {busy && <Loader2 className="size-3.5 animate-spin text-muted-foreground" />}
        <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onChange} />
      </div>
    </div>
  );
}

// ── Reset password (admin variant — no current_password) ─────────────────────

function ResetPasswordCard({ userId, username }: { userId: number; username: string }) {
  const { t } = useT();
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  function reset() { setNext(''); setConfirm(''); }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessMsg(null);

    if (next.length < PASSWORD_MIN) {
      setErrorMsg(t('account.passwordTooShort', { n: PASSWORD_MIN }));
      return;
    }
    if (next !== confirm) {
      setErrorMsg(t('account.passwordMismatch'));
      return;
    }

    setSubmitting(true);
    try {
      await adminSetUserPassword(userId, next);
      setSuccessMsg(t('admin.user.passwordResetSuccess', { username }));
      reset();
    } catch (err) {
      setErrorMsg(extractApiError(err, t('account.passwordRejected')));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card variant="section">
      <CardHeader>
        <CardTitle>{t('admin.user.password')}</CardTitle>
        <CardDescription>
          {t('admin.user.passwordDescription', { n: PASSWORD_MIN })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="grid gap-4 sm:max-w-sm">
          {errorMsg && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {errorMsg}
            </div>
          )}
          {successMsg && (
            <div className="rounded-md bg-emerald-100 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
              {successMsg}
            </div>
          )}
          <div className="grid gap-1.5">
            <Label htmlFor="admin-new-password">{t('account.passwordNew')}</Label>
            <Input
              id="admin-new-password"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={e => setNext(e.target.value)}
              required
              minLength={PASSWORD_MIN}
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="admin-confirm-password">{t('account.passwordConfirm')}</Label>
            <Input
              id="admin-confirm-password"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              required
              minLength={PASSWORD_MIN}
            />
          </div>
          <div>
            <Button type="submit" disabled={submitting}>
              {submitting ? t('account.passwordSubmitting') : t('admin.user.passwordSubmit')}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ── Branches editor ──────────────────────────────────────────────────────────

function BranchesCard({ user, onChanged }: { user: AdminUserDetail; onChanged: () => void }) {
  const { t } = useT();
  // Fetch the full zone catalog. Same key + fetcher as useCurrentZone, so we
  // share its cache — direct deep-link still works because SWR will fetch
  // when the cache is cold.
  const { data: allZonesResp } = useSWR(ZONES_SWR_KEY, () =>
    listZones(1000).then(r => r.data.results),
  );
  const allZones: ZoneDto[] = Array.isArray(allZonesResp) ? allZonesResp : [];

  const grantedZoneIds = useMemo(
    () => new Set(user.zone_memberships.map(m => m.zone_id)),
    [user.zone_memberships],
  );

  const availableZoneOptions: ComboboxOption[] = useMemo(
    () =>
      allZones
        .filter(z => !grantedZoneIds.has(Number(z.id)))
        .map(z => ({ value: String(z.id), label: z.humanreadable_name || `#${z.id}` })),
    [allZones, grantedZoneIds],
  );

  const [pickedZone, setPickedZone] = useState('');
  const [pickedRole, setPickedRole] = useState<string>('zone_employee');
  const [granting, setGranting] = useState(false);
  const [grantError, setGrantError] = useState<string | null>(null);

  async function handleGrant() {
    if (!pickedZone) return;
    setGrantError(null);
    setGranting(true);
    try {
      await grantUserZone(user.id, pickedZone, pickedRole);
      setPickedZone('');
      onChanged();
    } catch (err) {
      setGrantError(extractApiError(err, t('error.general')));
    } finally {
      setGranting(false);
    }
  }

  return (
    <Card variant="section">
      <CardHeader>
        <CardTitle>{t('admin.user.branches')}</CardTitle>
        <CardDescription>{t('admin.user.branchesDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {user.zone_memberships.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('admin.user.noBranches')}</p>
        )}
        {user.zone_memberships.length > 0 && (
          <div className="space-y-2">
            {user.zone_memberships.map(m => (
              <BranchRow
                key={m.zone_id}
                membership={m}
                userId={user.id}
                onChanged={onChanged}
                onRemoved={() => { onChanged(); globalMutate(ZONES_SWR_KEY); }}
              />
            ))}
          </div>
        )}

        <div className="space-y-2 border-t pt-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t('admin.user.grantBranch')}
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="min-w-[12rem] flex-1">
              <Combobox
                value={pickedZone}
                onChange={setPickedZone}
                options={availableZoneOptions}
                placeholder={
                  availableZoneOptions.length === 0
                    ? t('admin.user.noBranchesAvailable')
                    : t('admin.user.pickBranch')
                }
                disabled={availableZoneOptions.length === 0 || granting}
              />
            </div>
            <div className="w-44">
              <Select
                value={pickedRole}
                onChange={e => setPickedRole(e.target.value)}
                disabled={granting}
              >
                {ZONE_ROLES.map(r => (
                  <option key={r} value={r}>{t(`role.${r}`)}</option>
                ))}
              </Select>
            </div>
            <Button
              type="button"
              size="sm"
              className="gap-1.5"
              disabled={!pickedZone || granting}
              onClick={handleGrant}
            >
              <Plus className="size-3.5" />
              {granting ? t('common.saving') : t('admin.user.grant')}
            </Button>
          </div>
          {grantError && <p className="text-xs text-destructive">{grantError}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

function BranchRow({
  membership, userId, onChanged, onRemoved,
}: {
  membership: AdminUserZoneMembership;
  userId: number;
  onChanged: () => void;
  onRemoved: () => void;
}) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // VISITS-1 (#175): a row with no direct membership (id === null) exists
  // only because a group grants it — editing/revoking happens on the
  // group, not here. Alexey's decision: no separate "effective access"
  // screen, annotate this one instead ("inherited from group" + a lock).
  const locked = membership.locked || membership.id === null;
  const hasGroupOverlap = membership.inherited_from_groups.length > 0;

  async function handleRoleChange(e: React.ChangeEvent<HTMLSelectElement>) {
    if (membership.id === null) return;
    const next = e.target.value;
    setError(null);
    setBusy(true);
    try {
      await updateUserZoneRole(userId, membership.id, next);
      onChanged();
    } catch (err) {
      setError(extractApiError(err, t('error.general')));
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove() {
    if (membership.id === null) return;
    if (!window.confirm(t('admin.user.removeBranchConfirm', { zone: membership.zone_name }))) return;
    setError(null);
    setBusy(true);
    try {
      await revokeUserZone(userId, membership.id);
      onRemoved();
    } catch (err) {
      setError(extractApiError(err, t('error.general')));
      setBusy(false);
    }
  }

  return (
    <div className="space-y-1">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm">
        <div className="min-w-0 flex-1">
          <span className="block truncate font-medium">{membership.zone_name}</span>
          {hasGroupOverlap && (
            <span className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
              {locked && <Lock className="size-3" />}
              {t('admin.user.inheritedFromGroup', { groups: membership.inherited_from_groups.join(', ') })}
            </span>
          )}
        </div>
        <div className="w-44">
          {locked ? (
            <Tooltip content={t('admin.user.roleLockedHint')}><div
              className="flex h-8 items-center gap-1.5 rounded-md border bg-muted/40 px-2 text-xs text-muted-foreground"
            >
              <Lock className="size-3 shrink-0" />
              <span className="truncate">{t(`role.${membership.role}`)}</span>
            </div></Tooltip>
          ) : (
            <Select value={membership.role} onChange={handleRoleChange} disabled={busy} className="h-8 text-xs">
              {ZONE_ROLES.map(r => (
                <option key={r} value={r}>{t(`role.${r}`)}</option>
              ))}
            </Select>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          disabled={busy || locked}
          onClick={handleRemove}
          title={locked ? t('admin.user.roleLockedHint') : t('admin.user.removeBranch')}
          aria-label={t('admin.user.removeBranch')}
        >
          <Trash2 className="size-3.5 text-destructive" />
        </Button>
      </div>
      {error && <p className="px-3 text-xs text-destructive">{error}</p>}
    </div>
  );
}

// ── Telegram binding (operator-set pre-bind) ──────────────────────
//
// Lets the superuser bind a TG username to this Django user. The
// pre-bind is unverified until the user first logs in via the TG
// widget; the BE backfills telegram_id on that login and lands them
// on this account. Without a pre-bound row, a TG user whose handle
// happens to match this Django username CANNOT claim the account —
// the BE matcher requires the explicit operator binding.

function TelegramBindingCard({
  user,
  onChanged,
}: {
  user: AdminUserDetail;
  onChanged: () => void;
}) {
  const { t } = useT();
  const binding = user.telegram_binding;
  const [draft, setDraft] = useState(binding?.telegram_username ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const clean = draft.trim().replace(/^@/, '');
      if (!clean) {
        setError(t('admin.user.telegram.usernameRequired'));
        return;
      }
      await setAdminUserTelegramBinding(user.id, clean);
      onChanged();
    } catch (e) {
      setError(extractApiError(e, t('error.general')));
    } finally {
      setSaving(false);
    }
  };

  const onClear = async () => {
    setSaving(true);
    setError(null);
    try {
      await clearAdminUserTelegramBinding(user.id);
      setDraft('');
      onChanged();
    } catch (e) {
      setError(extractApiError(e, t('error.general')));
    } finally {
      setSaving(false);
    }
  };

  const draftDiffersFromSaved =
    draft.trim().replace(/^@/, '').toLowerCase() !==
    (binding?.telegram_username ?? '').toLowerCase();

  return (
    <Card variant="section">
      <CardContent className="space-y-3">
        <div>
          <h2 className="text-base font-semibold">{t('admin.user.telegram.title')}</h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t('admin.user.telegram.hint')}
          </p>
        </div>

        {binding && (
          <div className="flex items-center gap-2 text-xs">
            <span className="font-mono">@{binding.telegram_username || '—'}</span>
            {binding.verified ? (
              <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                {t('admin.user.telegram.verified')}
              </span>
            ) : (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                {t('admin.user.telegram.pending')}
              </span>
            )}
          </div>
        )}

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">@</span>
            <input
              type="text"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder={t('admin.user.telegram.placeholder')}
              className="h-9 w-full rounded-md border bg-background px-3 pl-6 text-sm"
              autoComplete="off"
            />
          </div>
          <Button
            size="sm"
            onClick={onSave}
            disabled={saving || !draftDiffersFromSaved || !draft.trim()}
          >
            {saving ? <Loader2 className="size-3.5 animate-spin" /> : t('admin.user.telegram.bind')}
          </Button>
          {binding && (
            <Button
              variant="outline"
              size="sm"
              onClick={onClear}
              disabled={saving}
            >
              {t('admin.user.telegram.unbind')}
            </Button>
          )}
        </div>

        {error && (
          <p className="text-xs text-destructive">{error}</p>
        )}
      </CardContent>
    </Card>
  );
}

// Группы пользователя (Алексей 22.10, #1969): добавить в группу прямо со страницы человека —
// группа даёт доступ ко всем филиалам своих групп филиалов.
function UserGroupsCard({ user, onChanged }: { user: AdminUserDetail; onChanged: () => void }) {
  const { data: groups } = useSWR('admin-user-groups', () => listUserGroups().then(r => r.data.results));
  const [picked, setPicked] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const mine = user.user_groups ?? [];
  const mineIds = new Set(mine.map(g => g.id));
  const options: ComboboxOption[] = (groups ?? []).filter(g => !mineIds.has(g.id)).map(g => ({ value: String(g.id), label: g.name }));
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); onChanged(); void globalMutate('admin-user-groups'); } catch (e) { setErr(extractApiError(e, 'Ошибка')); } finally { setBusy(false); }
  };
  return (
    <Card variant="section">
      <CardHeader>
        <CardTitle>Группы</CardTitle>
        <CardDescription>Группа открывает все филиалы своих групп филиалов, в том числе добавленные позже.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {mine.length === 0 && <p className="text-sm text-muted-foreground">Не состоит в группах.</p>}
        {mine.map(g => (
          <div key={g.id} className="flex items-center justify-between rounded-md bg-muted/30 px-3 py-1.5 text-sm">
            <span>{g.name}</span>
            <Button variant="ghost" size="icon-sm" disabled={busy} onClick={() => run(() => removeUserGroupMember(g.id, user.id))} aria-label="Убрать из группы">
              <Trash2 className="size-3.5" />
            </Button>
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <div className="min-w-[12rem] flex-1">
            <Combobox value={picked} onChange={setPicked} options={options} placeholder="Выбрать группу" disabled={busy || options.length === 0} />
          </div>
          <Button size="sm" className="gap-1.5" disabled={!picked || busy}
            onClick={() => run(async () => { await addUserGroupMember(Number(picked), user.id); setPicked(''); })}>
            <Plus className="size-3.5" /> Добавить в группу
          </Button>
        </div>
        {err && <p className="text-xs text-destructive">{err}</p>}
      </CardContent>
    </Card>
  );
}
